import { useEffect, useMemo, useState } from 'react';
import { getFileByIDQuery } from '../../../../../../db/api/get-all-files.js'; // Pfad ggf. anpassen
import type { DragItem, OrderedPage, SubmissionReviewProps } from '../utils.js';
import { UNASSIGNED_BUCKET_ID } from '../utils.js';
import { usePreviewLightbox } from './use-preview-lightbox.js';

function isNameRelatedSplitIssue(issue: string): boolean {
  const normalized = issue.trim().toLowerCase();
  return (
    normalized.includes('kein eindeutiger schülername gefunden') || normalized.includes('schülername ist unsicher')
  );
}

export function useSubmissionState(props: SubmissionReviewProps) {
  const { submissions, onSave, onCreateNewSubmission, onDeleteSubmission, lockingEnabled = false } = props;

  // Individually locked submissions (confirmed batch). New/unsaved buckets and the unassigned
  // bucket are never locked.
  const lockedIds = useMemo(
    () => new Set(lockingEnabled ? submissions.filter(s => s.splitConfirmed).map(s => s._id) : []),
    [submissions, lockingEnabled],
  );
  const isBucketLocked = (bucketId: string) => lockedIds.has(bucketId);

  // The whole canvas is read-only only once every submission is confirmed (nothing left to edit).
  const isReadOnly = lockingEnabled && submissions.length > 0 && submissions.every(s => s.splitConfirmed);

  const [pagesByStudent, setPagesByStudent] = useState<Record<string, OrderedPage[]>>({});
  const [editedNames, setEditedNames] = useState<Record<string, string>>({});
  const [splitConfidenceByStudent, setSplitConfidenceByStudent] = useState<Record<string, number>>({});
  const [splitIssuesByStudent, setSplitIssuesByStudent] = useState<Record<string, string[]>>({});
  const [newStudents, setNewStudents] = useState<Record<string, { tempId: string; name: string }>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const [fallbackUrls, setFallbackUrls] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [dropTarget, setDropTarget] = useState<DragItem | null>(null);

  const lightbox = usePreviewLightbox(pagesByStudent, fallbackUrls);

  // Initialize Buckets
  useEffect(() => {
    const initialBuckets: Record<string, OrderedPage[]> = { [UNASSIGNED_BUCKET_ID]: [] };
    const initialNames: Record<string, string> = {};
    const initialSplitConfidence: Record<string, number> = {};
    const initialSplitIssues: Record<string, string[]> = {};

    submissions.forEach((sub, index) => {
      const normalizedName = sub.studentName?.trim();
      initialNames[sub._id] = normalizedName || (sub.studentId ? `Student ${sub.studentId}` : `Schüler ${index + 1}`);
      initialSplitConfidence[sub._id] =
        typeof sub.splitEvaluationResult?.confidence === 'number' ? sub.splitEvaluationResult.confidence : 0;
      initialSplitIssues[sub._id] = Array.isArray(sub.splitEvaluationResult?.issues)
        ? sub.splitEvaluationResult.issues.map(issue => String(issue)).filter(issue => !isNameRelatedSplitIssue(issue))
        : [];

      const imageIds = sub.imageIds || [];
      const urls = sub.pages || [];

      initialBuckets[sub._id] = imageIds.map((imgId, i) => ({
        imageId: imgId,
        url: urls[i] || '',
        sourceSubmissionId: sub._id,
      }));
    });

    setPagesByStudent(initialBuckets);
    setEditedNames(initialNames);
    setSplitConfidenceByStudent(initialSplitConfidence);
    setSplitIssuesByStudent(initialSplitIssues);
  }, [submissions]);

  // Load Fallback URLs
  useEffect(() => {
    const allPages = Object.values(pagesByStudent).flat();
    const missingImageIds = Array.from(new Set(allPages.filter(p => !p.url && p.imageId).map(p => p.imageId)));
    if (missingImageIds.length === 0) return;

    let isMounted = true;
    const loadUrls = async () => {
      const results = await Promise.allSettled(
        missingImageIds.map(async id => {
          const file = await getFileByIDQuery(id)({ _id: 1, meta: { fileName: 1, extension: 1 } });
          return { id, url: file.getServeLink() };
        }),
      );
      if (!isMounted) return;
      setFallbackUrls(prev => {
        const next = { ...prev };
        results.forEach(r => {
          if (r.status === 'fulfilled') next[r.value.id] = r.value.url;
        });
        return next;
      });
    };
    void loadUrls();
    return () => {
      isMounted = false;
    };
  }, [pagesByStudent]);

  // Drag and Drop Logik
  const handleDragStart = (e: React.DragEvent, bucketId: string, index: number) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setDraggedItem({ bucketId, index });
    e.dataTransfer.effectAllowed = 'move';
    const crt = (e.currentTarget as HTMLElement).cloneNode(true) as HTMLElement;
    crt.style.opacity = '0.5';
    document.body.appendChild(crt);
    e.dataTransfer.setDragImage(crt, 20, 20);
    setTimeout(() => document.body.removeChild(crt), 0);
  };

  const handleDragOver = (e: React.DragEvent, bucketId: string, index: number) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget?.bucketId !== bucketId || dropTarget?.index !== index) {
      setDropTarget({ bucketId, index });
    }
  };

  const handleDrop = (e: React.DragEvent, targetBucketId: string, targetIndex: number) => {
    // Strict freeze: can't drop into a locked bucket, nor pull a page out of one.
    if (isReadOnly || isBucketLocked(targetBucketId) || (draggedItem && isBucketLocked(draggedItem.bucketId))) return;
    e.preventDefault();
    if (!draggedItem) return;

    setPagesByStudent(prev => {
      const next = { ...prev };
      const sourceList = [...(next[draggedItem.bucketId] || [])];
      const targetList = draggedItem.bucketId === targetBucketId ? sourceList : [...(next[targetBucketId] || [])];

      const [movedPage] = sourceList.splice(draggedItem.index, 1);
      if (!movedPage) return prev;

      let finalIndex = targetIndex;
      if (draggedItem.bucketId === targetBucketId && draggedItem.index < targetIndex) {
        finalIndex -= 1;
      }

      if (finalIndex === -1) targetList.push(movedPage);
      else targetList.splice(finalIndex, 0, movedPage);

      next[draggedItem.bucketId] = sourceList;
      next[targetBucketId] = targetList;
      return next;
    });

    setDraggedItem(null);
    setDropTarget(null);
  };

  // Actions
  const addNewStudent = () => {
    if (isReadOnly) return;
    const tempId = `new-${Date.now()}`;
    const studentCount = submissions.length + Object.keys(newStudents).length + 1;
    setNewStudents(prev => ({ ...prev, [tempId]: { tempId, name: `Neuer Schüler ${studentCount}` } }));
    setPagesByStudent(prev => ({ ...prev, [tempId]: [] }));
  };

  const deleteStudent = (bucketId: string, isNew: boolean) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setPagesByStudent(prev => {
      const next = { ...prev };
      const orphanedPages = next[bucketId] || [];
      next[UNASSIGNED_BUCKET_ID] = [...(next[UNASSIGNED_BUCKET_ID] || []), ...orphanedPages];
      delete next[bucketId];
      return next;
    });

    if (isNew) {
      setNewStudents(prev => {
        const n = { ...prev };
        delete n[bucketId];
        return n;
      });
    } else {
      setDeletedIds(prev => new Set(prev).add(bucketId));
    }
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    if ((pagesByStudent[UNASSIGNED_BUCKET_ID]?.length ?? 0) > 0) {
      setError('Bitte weise zuerst alle Seiten in der Ablage einem Schüler zu.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const tempIdToRealId = new Map<string, string>();
      for (const [tempId, data] of Object.entries(newStudents)) {
        if (onCreateNewSubmission) {
          const imageIds = pagesByStudent[tempId]?.map(p => p.imageId) || [];
          const realId = await onCreateNewSubmission(data.name, imageIds);
          if (realId) tempIdToRealId.set(tempId, realId);
        }
      }

      const reassignmentPayload = [
        ...submissions
          .filter(s => !deletedIds.has(s._id))
          .map(s => ({
            submissionId: s._id,
            imageIds: pagesByStudent[s._id]?.map(p => p.imageId) || [],
            studentName: editedNames[s._id],
          })),
        ...Object.keys(newStudents).map(tempId => {
          const newStudent = newStudents[tempId];
          return {
            submissionId: tempIdToRealId.get(tempId) || tempId,
            imageIds: pagesByStudent[tempId]?.map(p => p.imageId) || [],
            studentName: newStudent?.name ?? '',
          };
        }),
      ];

      await onSave(reassignmentPayload);

      if (deletedIds.size > 0 && onDeleteSubmission) {
        await Promise.all(Array.from(deletedIds).map(id => onDeleteSubmission(id)));
      }

      setNewStudents({});
      setDeletedIds(new Set());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern.');
    } finally {
      setIsSaving(false);
    }
  };

  const allBuckets = useMemo(() => {
    const original = submissions
      .filter(s => !deletedIds.has(s._id))
      .map(s => ({ id: s._id, isNew: false, locked: lockedIds.has(s._id) }));
    const added = Object.keys(newStudents).map(id => ({ id, isNew: true, locked: false }));
    return [...original, ...added];
  }, [submissions, deletedIds, newStudents, lockedIds]);

  const unassignedPages = pagesByStudent[UNASSIGNED_BUCKET_ID] || [];

  const updateNewStudentName = (bucketId: string, name: string) => {
    if (isReadOnly) return;
    setNewStudents(prev => ({
      ...prev,
      [bucketId]: { tempId: prev[bucketId]?.tempId || bucketId, name },
    }));
  };

  const updateEditedName = (bucketId: string, name: string) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setEditedNames(prev => ({ ...prev, [bucketId]: name }));
  };

  return {
    state: {
      pagesByStudent,
      editedNames,
      splitConfidenceByStudent,
      splitIssuesByStudent,
      newStudents,
      fallbackUrls,
      isSaving,
      error,
      isReadOnly,
      draggedItem,
      dropTarget,
      allBuckets,
      unassignedPages,
    },
    actions: {
      handleDragStart,
      handleDragOver,
      handleDrop,
      addNewStudent,
      deleteStudent,
      handleSave,
      updateEditedName,
      updateNewStudentName,
      setEditedNames,
      setNewStudents,
    },
    lightbox,
  };
}

export type SubmissionReviewStore = ReturnType<typeof useSubmissionState>;
